import React, { useState } from 'react'
import { xinyiHowToGetHere } from './content'
import type { Lang } from './types'

interface XinyiHowToGetHereV2Props {
  currentLang: Lang
}

/**
 * XinyiHowToGetHereV2
 * Minimal expandable card (border + + / − toggle) — Barista Map style.
 * Inside: walking narrative, 3 navigation links, embedded Google Maps.
 */
const XinyiHowToGetHereV2: React.FC<XinyiHowToGetHereV2Props> = ({ currentLang }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <section className="bg-[#FAF9F1] py-16 md:py-24">
      <div className="mx-auto max-w-[1100px] px-6 md:px-10">
        <div className="mb-8 text-center">
          <p
            className="mb-3 text-[12px] tracking-[0.22em] text-black/65"
            style={{
              fontFamily: "'EB Garamond', serif",
              fontStyle: 'italic',
              fontWeight: 400,
            }}
          >
            {xinyiHowToGetHere.eyebrow[currentLang]}
          </p>
          <h2
            className="text-black"
            style={{
              fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
              fontWeight: 400,
              fontSize: 'clamp(1.5rem, 2.6vw, 2rem)',
              letterSpacing: '0.04em',
              lineHeight: 1.3,
            }}
          >
            {xinyiHowToGetHere.title[currentLang]}
          </h2>
        </div>

        <div className="border border-black/15">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-black/[0.02]"
          >
            <span
              className="text-sm tracking-[0.18em] text-black"
              style={{ fontFamily: "'EB Garamond', 'Noto Serif TC', serif" }}
            >
              {currentLang === 'zh'
                ? '從台北 101／信義商圈怎麼到 ONGU Studio 信義店'
                : 'From Taipei 101 / Xinyi District to ONGU Studio Xinyi'}
            </span>
            <span className="text-xl text-black/50">{expanded ? '−' : '+'}</span>
          </button>

          {expanded && (
            <div className="border-t border-black/10 px-6 py-10 md:px-10 md:py-12">
              <div
                className="mb-10 space-y-5 text-[15px] leading-loose text-black/75"
                style={{
                  fontFamily: "'Noto Serif TC', 'Noto Sans TC', sans-serif",
                  fontWeight: 300,
                  letterSpacing: '0.03em',
                }}
              >
                {xinyiHowToGetHere.paragraphs.map((p, i) => (
                  <p key={i}>{p[currentLang]}</p>
                ))}
              </div>

              <div className="mb-10 grid gap-3 md:grid-cols-3">
                {xinyiHowToGetHere.links.map((link, i) => (
                  <a
                    key={i}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border border-black px-5 py-3 text-center text-[12px] tracking-[0.22em] text-black transition-colors hover:bg-black hover:text-white"
                    style={{ fontFamily: "'EB Garamond', serif" }}
                  >
                    {link.label[currentLang].toUpperCase()}
                  </a>
                ))}
              </div>

              <div className="aspect-video w-full overflow-hidden bg-black/5">
                <iframe
                  src={xinyiHowToGetHere.mapEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={xinyiHowToGetHere.mapTitle[currentLang]}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default XinyiHowToGetHereV2
